import { useAuth } from 'oidc-react';
import { getQueryFn, useApiQuery, ApiFetchParams } from '../helpers/useApi';
import { apiFetch } from './fetch';

export const BANNER_ACTION_INTERESTED = 'interested' as const;
export type BannerAction = typeof BANNER_ACTION_INTERESTED;

export interface Banner {
    id: string;
    feature: string;
    type: string;
    displayPages: string[];
    startDate: string;
    endDate: string;
}

export interface BannerUserAction {
    bannerId: string;
    feature: string;
    action: BannerAction;
    actionDate: string;
}

export interface UseBannerUserActionsResult {
    bannerUserActions: BannerUserAction[];
    hasCheckedAPI: boolean;
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
}

export const useActiveBanners = (pageName: string) => {
    const auth = useAuth();
    const maybeAccessToken = auth?.userData?.access_token;

    return useApiQuery<Banner[]>(
        {
            path: '/data/banner/v1/banners',
            body: {},
            method: 'GET',
        },
        `activeBanners-${pageName}`,
        {
            select: (banners) => banners.filter((banner) => banner.displayPages.includes(pageName)),
            enabled: Boolean(maybeAccessToken),
        }
    );
};


export const useBannerUserActions = (): UseBannerUserActionsResult => {
    const auth = useAuth();
    const accessToken = auth?.userData?.access_token;

    const {
        data: bannerUserActions = [],
        isLoading,
        isError,
        refetch,
    } = useApiQuery<BannerUserAction[]>(
        {
            path: '/data/banner/v1/banner-user-actions',
            body: {},
        } as ApiFetchParams,
        `bannerUserActions`,
        {
            enabled: Boolean(accessToken),
        }
    );

    const hasCheckedAPI = accessToken ? !isLoading : true;

    return {
        bannerUserActions,
        hasCheckedAPI,
        isLoading,
        isError,
        refetch,
    };
};

export interface BannerActionRequest {
    action: BannerAction;
}

export function respondToBanner(bannerId: string, action: BannerAction, accessToken: string | undefined): Promise<object> {
    return getQueryFn<object>(
        {
            path: `/data/banner/v1/banners/${bannerId}/action`,
            body: { action },
            method: 'POST',
        },
        (body: unknown = {}, path = '/', method = 'GET', endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY, authorization: string | undefined = undefined) => apiFetch<object>(path, body, method, endpoint, authorization || `Bearer ${accessToken}`),
        accessToken
    )();
}
